import { useEffect, useRef, useState } from 'react';
import { COGNOTA_ORIGIN, COGNOTA_REQUEST_MODAL_URL } from 'utils/cognotaUrls';
import { getRequestRoute } from 'utils/dynamicRoutes';
import {
  isIncomingCognotaMessage,
  OutgoingCognotaMessage,
} from './postMessageProtocol';

type Props = {
  isOpen: boolean;
  requestId: string | null;
  onClose: () => void;
  preload?: boolean;
};

const PRELOAD_TIMEOUT_MS = 500;

const AdvancedRequestModalIframe = ({
  isOpen,
  requestId,
  onClose,
  preload = false,
}: Props) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [hasOpened, setHasOpened] = useState(isOpen);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isOpen) setHasOpened(true);
  }, [isOpen]);

  useEffect(() => {
    if (!preload || hasOpened) return;
    const ric = window.requestIdleCallback;
    if (typeof ric === 'function') {
      const id = ric(() => setHasOpened(true));
      return () => window.cancelIdleCallback?.(id);
    }
    const timeoutId = window.setTimeout(
      () => setHasOpened(true),
      PRELOAD_TIMEOUT_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [preload, hasOpened]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== COGNOTA_ORIGIN) return;
      if (!isIncomingCognotaMessage(event.data)) return;

      if (event.data.type === 'request-modal-ready') {
        setIsReady(true);
        return;
      }

      if (event.data.type === 'request-modal-closed') {
        onClose();
        return;
      }

      if (event.data.type === 'open-request-fullpage') {
        window.open(
          getRequestRoute(event.data.requestId, null, { isJsonSchema: true }),
          '_blank',
          'noopener,noreferrer'
        );
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || !requestId || !isReady) return;

    const message: OutgoingCognotaMessage = {
      type: 'open-request-modal',
      requestId,
    };
    iframeRef.current?.contentWindow?.postMessage(message, COGNOTA_ORIGIN);
  }, [isOpen, requestId, isReady]);

  if (!hasOpened) return null;

  return (
    <iframe
      ref={iframeRef}
      data-testid='advanced-request-modal-iframe'
      data-cy='advanced-request-modal-iframe'
      title='View Request'
      src={COGNOTA_REQUEST_MODAL_URL}
      className='z-50'
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        border: 0,
        display: isOpen ? 'block' : 'none',
      }}
    />
  );
};

export default AdvancedRequestModalIframe;
